/**
 * AdminRecycledLockedPanel — Feb 2026 (AORT bug follow-up).
 *
 * Lists every ticker that was moved to non-dividend via the Recycled
 * Tickers modal and now carries a permanent `recycled_lock`. These
 * tickers CANNOT be moved back to the dividend universe by any admin
 * button — the only escape hatches are the blocklist trash (bad-stock
 * DB) or a main-agent unlock. This panel gives the admin a one-glance
 * view of every locked row + a copy-to-clipboard hint for asking the
 * agent to unlock a specific ticker.
 *
 * Data source: `GET /api/admin/recycled-locked-tickers` (admin-only).
 * Rows show `>` chevron (visual reinforcement of the same marker
 * rendered inline across StockModal / Pending Non-Div Review), the
 * lock timestamp, and the recorded reason / notes.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Lock, X, ClipboardCopy, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return String(iso).slice(0, 10); }
};

export default function AdminRecycledLockedPanel({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/recycled-locked-tickers`,
        { withCredentials: true },
      );
      setLocked(Array.isArray(data?.locked) ? data.locked : []);
    } catch (err) {
      toast.error('Could not load recycled-locked tickers', {
        description: err?.response?.data?.detail || err?.message,
      });
      setLocked([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const openModal = () => {
    setOpen(true);
    load();
  };
  const closeModal = () => setOpen(false);

  const copyUnlockRequest = async (ticker) => {
    const msg = `Please unlock ticker ${ticker} — I've verified with an external source that its former symbol was NOT recycled to a different security, so the recycled_lock is a false positive. Remove it from recycled_locked_tickers.`;
    try {
      await navigator.clipboard.writeText(msg);
      toast.success(`Copied unlock request for ${ticker}`, {
        description: 'Paste it to the main agent in chat to lift the lock.',
        duration: 5000,
      });
    } catch (_e) {
      toast.error('Clipboard blocked', {
        description: `Manually copy: ${msg}`,
      });
    }
  };

  const openTicker = (ticker, e) => {
    if (e) e.preventDefault();
    if (typeof onStockClick === 'function') {
      onStockClick(ticker);
    } else {
      window.dispatchEvent(new CustomEvent('open-stock-modal', {
        detail: { ticker: String(ticker).toUpperCase() },
      }));
    }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-700 hover:bg-amber-800 text-white transition-colors"
        data-testid="admin-recycled-locked-panel-btn"
        title="View every ticker permanently locked out of the dividend universe by the Recycled Tickers admin sweep."
      >
        <Lock className="w-3 h-3" />
        Recycled-Locked Tickers
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={closeModal}
          data-testid="admin-recycled-locked-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <Lock className="w-4 h-4 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">Recycled-Locked Tickers</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  Locked out of the dividend universe. Only the blocklist trash button — or a main-agent unlock — can remove the lock.
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-recycled-locked-close"
                aria-label="Close recycled-locked panel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              )}

              {!loading && locked.length === 0 && (
                <div
                  className="px-3 py-4 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-sm"
                  data-testid="admin-recycled-locked-empty"
                >
                  No tickers are recycled-locked. Locks are created when you click the arrow on a row inside the Recycled Tickers modal.
                </div>
              )}

              {!loading && locked.length > 0 && (
                <>
                  <div
                    className="px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-[11px] leading-relaxed"
                    data-testid="admin-recycled-locked-hint"
                  >
                    Click the <b>clipboard icon</b> to copy an unlock request for the main agent. Paste it into chat, and the agent will lift the lock after verifying the former symbol was NOT recycled. Trashing the ticker (bad-stock DB) from the Recycled Tickers modal also removes the lock, but that&apos;s a one-way destructive action.
                  </div>

                  <ul className="space-y-2" data-testid="admin-recycled-locked-list">
                    {locked.map((row) => (
                      <li
                        key={row.ticker}
                        className="flex items-start justify-between gap-3 px-3 py-2 rounded-md border border-border/60 bg-muted/10"
                        data-testid={`admin-recycled-locked-row-${row.ticker}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <button
                              type="button"
                              onClick={(e) => openTicker(row.ticker, e)}
                              className="font-mono font-bold text-sm text-amber-700 dark:text-amber-300 hover:underline inline-flex items-center gap-1"
                              title={`Open ${row.ticker} in the stock modal`}
                              data-testid={`admin-recycled-locked-ticker-${row.ticker}`}
                            >
                              <span className="text-amber-600 dark:text-amber-400">&gt;</span>
                              {row.ticker}
                              <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                            </button>
                            <span className="text-[10px] text-muted-foreground">
                              locked {fmtDate(row.locked_at)} by {row.locked_by || 'admin'}
                            </span>
                          </div>
                          {(row.reason || row.notes) && (
                            <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                              {row.reason && (
                                <div>
                                  <span className="font-mono text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">reason:</span>{' '}
                                  {row.reason}
                                </div>
                              )}
                              {row.notes && (
                                <div className="italic mt-0.5">{row.notes}</div>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => copyUnlockRequest(row.ticker)}
                          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-bold bg-violet-600 hover:bg-violet-700 text-white"
                          title={`Copy an unlock request for ${row.ticker} — paste it into chat to ask the main agent to lift the lock.`}
                          data-testid={`admin-recycled-locked-copy-${row.ticker}`}
                        >
                          <ClipboardCopy className="w-3 h-3" />
                          Ask agent to unlock
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="px-4 py-2 border-t border-border/60 text-[10px] text-muted-foreground flex items-center justify-between">
              <span>{locked.length} locked</span>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-1 text-violet-500 hover:text-violet-400 disabled:opacity-50"
                data-testid="admin-recycled-locked-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
